import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BaseObjectFields, AmountFields } from './shared/ObjectFieldComponents';
import {
    MetalFields,
    StonePercentField,
    StoneWeightField,
    ProposedField,
    PxCheckbox,
} from './shared/MetalFieldComponents';

interface MetalObject {
    id: string;
    enabled: boolean;
    denumire: string;
    tipMetal: string;
    karat: number;
    cantitate: number;
    um: string;
    pretUnitar: number;
    sumainLei: number;
    sumainEuro: number;
    piatraPercent: number;
    piatra: number;
    propus: number;
    px: boolean;
    categoria: number;
}

interface MetalObjectRowProps {
    obj: MetalObject;
    originalIndex: number;
    stonePercentOptions: number[];
    onObjectChange: (index: number, field: keyof MetalObject, value: unknown) => void;
    isCustomMetalRow?: boolean;
    metalTypeOptions?: string[];
    karatOptions?: number[];
    onCustomMetalSelectionChange?: (index: number, metalType: string, karat: number) => void;
    showStoneFields?: boolean;
    /** Keeps unit price and values as priced by the desk (session verification). */
    lockPrices?: boolean;
    /** Reads the scale into this row's quantity; absent when the workstation has no scale. */
    onReadScale?: () => void;
    /** A scale read for this row is running. */
    scaleReading?: boolean;
}

const MetalObjectRow = ({
    obj,
    originalIndex,
    stonePercentOptions,
    onObjectChange,
    isCustomMetalRow = false,
    metalTypeOptions = [],
    karatOptions = [],
    onCustomMetalSelectionChange,
    showStoneFields = true,
    lockPrices = false,
    onReadScale,
    scaleReading = false,
}: MetalObjectRowProps) => {
    const { t } = useTranslation();

    const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        // Allow empty string for clearing the field
        const numValue = value === '' ? 0 : Number(value);
        if (!isNaN(numValue) && numValue >= 0) {
            onObjectChange(originalIndex, 'cantitate', numValue);
        }
    };

    const handleFieldChange = (field: string, value: unknown) => {
        onObjectChange(originalIndex, field as keyof MetalObject, value);
    };

    const handleCustomMetalChange = (metalType: string) => {
        onCustomMetalSelectionChange?.(originalIndex, metalType, 0);
    };

    const handleCustomKaratChange = (karat: number) => {
        onCustomMetalSelectionChange?.(originalIndex, obj.tipMetal, karat);
    };

    const baseFields = isCustomMetalRow ? (
        <>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={obj.enabled}
                        onChange={e => handleFieldChange('enabled', e.target.checked)}
                    />
                }
                label=""
            />
            <FormControl size="small" sx={{ minWidth: 90 }}>
                <InputLabel>{t('contracts.metalType')}</InputLabel>
                <Select
                    value={obj.tipMetal}
                    onChange={event => handleCustomMetalChange(String(event.target.value))}
                    label={t('contracts.metalType')}
                    disabled={!obj.enabled}
                >
                    <MenuItem value="">
                        <em>{t('contracts.selectMetal')}</em>
                    </MenuItem>
                    {metalTypeOptions.map(metalType => (
                        <MenuItem key={metalType} value={metalType}>
                            {metalType}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>{t('contracts.karat')}</InputLabel>
                <Select
                    value={obj.karat > 0 ? obj.karat : ''}
                    onChange={event => handleCustomKaratChange(Number(event.target.value))}
                    label={t('contracts.karat')}
                    disabled={!obj.enabled || karatOptions.length === 0}
                >
                    <MenuItem value="">
                        <em>{t('contracts.selectKarat')}</em>
                    </MenuItem>
                    {karatOptions.map(karat => (
                        <MenuItem key={karat} value={karat}>
                            {karat}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </>
    ) : (
        <BaseObjectFields
            enabled={obj.enabled}
            denumire={obj.denumire}
            onFieldChange={handleFieldChange}
        />
    );

    return (
        <Box key={obj.id} mb={1}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                {baseFields}
                <TextField
                    size="small"
                    label={t('contracts.um')}
                    value={obj.um}
                    disabled={!obj.enabled}
                    slotProps={{ input: { readOnly: true } }}
                    sx={{ width: 70 }}
                />
                <AmountFields
                    enabled={obj.enabled}
                    pretUnitar={obj.pretUnitar}
                    sumainLei={obj.sumainLei}
                    sumainEuro={obj.sumainEuro}
                    onFieldChange={handleFieldChange}
                    lockUnitPrice={lockPrices}
                    lockAmounts={lockPrices}
                    afterUnitPrice={
                        <>
                            <MetalFields
                                enabled={obj.enabled}
                                cantitate={obj.cantitate}
                                onFieldChange={handleFieldChange}
                                handleQuantityChange={handleQuantityChange}
                                onReadScale={onReadScale}
                                scaleReading={scaleReading}
                            />
                            {/* Owner, 2026-11-03: the proposed value right after the quantity and scale. */}
                            <ProposedField enabled={obj.enabled} propus={obj.propus} />
                        </>
                    }
                />
                {showStoneFields && (
                    <>
                        <StonePercentField
                            enabled={obj.enabled}
                            piatraPercent={obj.piatraPercent}
                            stonePercentOptions={stonePercentOptions}
                            onFieldChange={handleFieldChange}
                        />
                        <StoneWeightField enabled={obj.enabled} piatra={obj.piatra} />
                    </>
                )}
                <PxCheckbox enabled={obj.enabled} px={obj.px} onFieldChange={handleFieldChange} />
            </Stack>
        </Box>
    );
};

export default MetalObjectRow;
